/* ---------------------------------------------------------------------------
   Who may do what: the eight roles, the things they are permissions for, and
   the map between them. LIFECYCLE.md §2 is the specification; this is it in
   code.

   ONE FILE, BOTH SIDES. functions/lib/roles.ts enforces this map on every /api
   request and the screens read it to decide what to draw, both through this
   module — the same arrangement src/data/rates.ts has with
   functions/lib/rateTables.ts, and for the stronger version of the same reason.
   A client that thought an inspector could import a spreadsheet would draw a
   button that 403s; a server that thought they could would let them. Two copies
   of a permission table drift in one of those two directions, and the second one
   is a hole.

   THE CLIENT COPY DECIDES WHAT TO DRAW, NEVER WHAT IS ALLOWED. Everything under
   /api resolves the roles again from the database and refuses on its own
   authority — see the route table in functions/lib/roles.ts. Hiding a button is a
   courtesy to somebody who cannot use it, not a security measure.

   AN ACCOUNT HOLDS ANY NUMBER OF ROLES, and may do the union of what they
   permit. Each role is one part of a bridge's life — planning it, building it,
   supervising the building, inspecting it, keeping it, rating it, deciding on
   it — and a person in a Division often does two of those. One role per account
   forced the register to invent `engineer` as the sum of five jobs, and then
   nothing could stop one person rating a bridge and accepting their own rating.

   HOLDING TWO ROLES NEVER LETS SOMEBODY ACCEPT THEIR OWN WORK. That is not
   expressed in this table and cannot be: it is a rule about a record and who
   made it, and the server applies it record by record — see
   functions/lib/duties.ts.

   NO ROLES IS NOT A ROLE, AND NEITHER IS SUSPENSION. An account with no roles
   reads everything and writes nothing — what `viewer` used to be. A suspended
   account keeps its roles and may do nothing at all, including reading; lifting
   the suspension gives them back. Suspension was a role once, which meant
   suspending somebody forgot what they had been, and lifting it was a second
   decision about what to grant them.

   WHAT IS DELIBERATELY NOT HERE. A role carries no district. It would be easy to
   write `engineer of Chitwan` into this table and much harder to make it true:
   every write endpoint would need to know which district the record it is
   touching belongs to, an import touches many at once, and a rate table is
   approved for a district by a division that spans several. A half-enforced
   geographical limit reads on screen as a limit and is not one — so the People
   screen records somebody's division as a note about them.
   --------------------------------------------------------------------------- */

/**
 * The eight roles, in the order a bridge's life meets them after the
 * administrator: planned, built, supervised, inspected, kept, rated, decided.
 *
 * The order is load-bearing in one place only: the People screen and the Guide
 * list them in it. Nothing decides a permission by comparing positions in this
 * array — no role is a subset of another.
 */
export const ROLES = [
  'admin',
  'planner',
  'site',
  'resident',
  'inspector',
  'engineer',
  'rater',
  'approver',
] as const;

export type Role = (typeof ROLES)[number];

/** Whether a string off the wire, or out of a database, is one of the eight. */
export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

/**
 * A set of roles off the wire, validated, deduplicated and in ROLES order.
 *
 * Null where the value is not a list of roles at all, so a caller can refuse it
 * rather than silently granting less than was asked. The order is ROLES' order
 * rather than the caller's, so two lists holding the same roles compare equal.
 */
export function readRoles(value: unknown): Role[] | null {
  if (!Array.isArray(value)) return null;
  if (!value.every(isRole)) return null;
  const held = new Set<Role>(value);
  return ROLES.filter((role) => held.has(role));
}

/* --------------------------- capabilities --------------------------- */

/**
 * The things a role is permission for.
 *
 * Each one is an act this application performs, named for the act rather than
 * for the endpoint or the button — `defect.withdraw`, not `DELETE /api/defects`
 * and not `the trash icon`. Endpoints get renamed and buttons get moved; what
 * withdrawing a defect means to a Division does not.
 *
 * Several are declared before any route uses them — the calendar, the
 * structure record, the handover, the status decisions. They are in the table
 * now so the People screen shows what granting a role will mean once those
 * screens exist, and so the role a person is given today is the one they will
 * need then.
 */
export const CAPABILITIES = [
  'register.read',
  'people.manage',
  'calendar.manage',
  'structure.create',
  'model.manage',
  /* The programme. Freezing a baseline and matching its activities to what is
     recorded on site are decisions about how this register will MEASURE the
     works — engineering judgement about a programme rather than a check on a
     field entry. Approving a baseline, a revision or an extension of time is
     the client's decision, and is a separate capability for the reason every
     accept is. */
  'programme.manage',
  'programme.approve',
  /* The construction record. Recording and reviewing are separate: an entry
     nobody but its author has looked at is not a verified record. */
  'progress.record',
  'progress.review',
  'works.supervise',
  'handover.prepare',
  'handover.accept',
  'inspection.log',
  'inspection.accept',
  'defect.log',
  'defect.progress',
  'defect.verify',
  'defect.withdraw',
  'inventory.edit',
  'inventory.import',
  /* The supporting reference tables the estimate is built from: the approved
     district rates and the wording of recommended work. */
  'reference.manage',
  /* Bridge Rating. Computing a rating and signing one off are separate for the
     same reason logging and accepting an inspection are: a posting decision that
     nobody but its author has looked at is not a decision. */
  'rating.compute',
  'rating.accept',
  'status.decide',
] as const;

export type Capability = (typeof CAPABILITIES)[number];

/**
 * What each capability lets somebody do, in the words the Guide and the People
 * screen both print.
 *
 * One sentence, naming the control where there is one. This is read by people
 * deciding what to grant a colleague, so it has to describe what will actually
 * happen and not a category of thing.
 */
export const CAPABILITY_LABEL: Record<Capability, string> = {
  'register.read':
    'Read the register — every structure, inspection, issue, photograph, model and printed document.',
  'people.manage': 'Grant and withdraw roles, and suspend accounts, on the People screen.',
  'calendar.manage': 'Keep the department’s working calendars and settings.',
  'structure.create': 'Create the record of a planned structure.',
  'model.manage': 'Import a 3D model onto a record, and delete one.',
  'programme.manage':
    'Freeze a baseline programme from the 3D model, revise it, and match its activities to what ' +
    'is recorded on site — which is what lets the register state a variance.',
  'programme.approve': 'Accept a baseline programme, a revision or an extension of time.',
  'progress.record':
    'Record the site record: what was built, the daily return, an incident, and the ' +
    'certificates and delivery notes that go with them.',
  'progress.review':
    'Move a construction entry or a daily return through review and accept it — the register ' +
    'records who did, and an accepted daily return is frozen.',
  'works.supervise':
    'Release hold points, raise and close non-conformance reports and requests for ' +
    'information, and close incidents.',
  'handover.prepare': 'Prepare the handover of a completed structure.',
  'handover.accept': 'Accept a handover, which puts the structure in service.',
  'inspection.log': 'Log an inspection against a structure.',
  'inspection.accept':
    'Move a logged inspection through review, and accept it — the register records who did.',
  'defect.log': 'Log an issue, on the record or on the 3D model in the Issue Inspector.',
  'defect.progress': 'Move an issue to In Review or Resolved, and record its repair.',
  'defect.verify': 'Verify a repaired issue on a later visit.',
  'defect.withdraw': 'Withdraw an issue that should not exist — a duplicate, or the wrong structure.',
  'inventory.edit':
    'Correct the inventory record, and set a structure’s position and deck axis on the map.',
  'inventory.import': 'Import inventory rows from a spreadsheet.',
  'reference.manage':
    'Keep the reference tables: load or withdraw a division’s approved district rates, and ' +
    'reword the recommended work for each issue.',
  'rating.compute':
    'Compute a load rating for a structure in Bridge Rating, save it as a draft, and send it ' +
    'for review.',
  'rating.accept':
    'Accept a load rating, or return it — which is what makes a posting decision of record.',
  'status.decide': 'Restrict, lift, close or reopen a bridge, with a reason.',
};

/* --------------------------- the map --------------------------- */

/**
 * What each role may do. LIFECYCLE.md §2's matrix, row for row.
 *
 * Written out per role rather than built by extending another, because no role
 * here is a superset of any other — and the check in `npm test` compares each
 * row against what the screens offer.
 *
 * Every role reads the register. So does an account with no role at all; see
 * NO_ROLE_CAPABILITIES.
 */
export const ROLE_CAPABILITIES: Record<Role, readonly Capability[]> = {
  /* Administers, and does not engineer. An administrator who also plans or
     rates holds those roles as well, granted like anyone else's and shown on
     the People screen — so the list of who may accept a rating is never
     "the approvers, and also whoever runs the application". */
  admin: ['register.read', 'people.manage', 'calendar.manage', 'reference.manage'],

  /* The contractor's or consultant's planning engineer. */
  planner: ['register.read', 'structure.create', 'model.manage', 'programme.manage'],

  /* The site agent: the foreman or inspector of works on a structure being
     built. Records what was done and reads the register.

     `defect.log` is not here, and that is the deliberate line. A crack in a
     pier that is still being built is a non-conformance against the works; a
     defect is a finding on a structure in service. */
  site: ['register.read', 'progress.record'],

  /* The supervision consultant on site: reviews the site record and supervises
     the works. */
  resident: [
    'register.read',
    'progress.record',
    'progress.review',
    'works.supervise',
    'handover.prepare',
  ],

  /* The bridge inspector: records what they found, and verifies a repair on a
     later visit. `inspection.accept` and `defect.progress` are not here — where
     a defect has got to is a decision about remedial work, and accepting an
     inspection is somebody else's review of it. */
  inspector: ['register.read', 'progress.record', 'inspection.log', 'defect.log', 'defect.verify'],

  /* The divisional or maintenance engineer: keeps the register. */
  engineer: [
    'register.read',
    'structure.create',
    'model.manage',
    'inspection.accept',
    'defect.log',
    'defect.progress',
    'defect.verify',
    'defect.withdraw',
    'inventory.edit',
    'inventory.import',
  ],

  /* The engineer who runs load ratings. Computes; does not accept. */
  rater: ['register.read', 'rating.compute'],

  /* The department's representative who decides. */
  approver: [
    'register.read',
    'programme.approve',
    'handover.accept',
    'rating.accept',
    'status.decide',
  ],
};

/**
 * What an account with no roles may do: read, and nothing else.
 *
 * Not an empty list, on purpose. The alternative defaults are both wrong: no
 * access at all locks out everybody the moment an account is made and before
 * anybody has granted it anything, and full access makes the whole table
 * decoration. Saying no to somebody is suspending them.
 */
export const NO_ROLE_CAPABILITIES: readonly Capability[] = ['register.read'];

/** Whether one role carries a capability. The People screen's grid asks this. */
export function roleCan(role: Role, capability: Capability): boolean {
  return ROLE_CAPABILITIES[role].includes(capability);
}

/**
 * Everything a set of roles permits, in CAPABILITIES order.
 *
 * The union of each role's row, plus reading for any account that is not
 * suspended. A suspended account gets nothing, whatever roles it holds — that
 * is what suspension is.
 */
export function capabilitiesOf(roles: readonly Role[], suspended = false): Capability[] {
  if (suspended) return [];
  const held = new Set<Capability>(NO_ROLE_CAPABILITIES);
  for (const role of roles) for (const capability of ROLE_CAPABILITIES[role]) held.add(capability);
  return CAPABILITIES.filter((capability) => held.has(capability));
}

/** Whether a set of roles carries a capability. The one question this module exists to answer. */
export function rolesCan(roles: readonly Role[], capability: Capability, suspended = false): boolean {
  if (suspended) return false;
  if (NO_ROLE_CAPABILITIES.includes(capability)) return true;
  return roles.some((role) => ROLE_CAPABILITIES[role].includes(capability));
}

/**
 * The same question, for roles that may not be known yet.
 *
 * Null is *the application has not been told* — the register has not answered
 * /api/health, or this deployment has nobody for it to be told about. Both
 * resolve to `true`, which is the behaviour every screen had before roles
 * existed; see rolesStatus in functions/lib/roles.ts, which reports it.
 */
export function can(
  roles: readonly Role[] | null,
  capability: Capability,
  suspended = false,
): boolean {
  return roles === null ? true : rolesCan(roles, capability, suspended);
}

/* --------------------------- how they read --------------------------- */

export interface RoleCopy {
  /** What it is called on screen. */
  label: string;
  /** Who holds it, and their part of the lifecycle, in one line. */
  who: string;
  /** What is deliberately withheld from somebody who holds this role alone. */
  not: string | null;
}

/**
 * The words for each role, in one place because several screens print them: the
 * People screen's checkboxes, the Guide's table, and the title bar's line under
 * the signed-in name.
 */
export const ROLE_COPY: Record<Role, RoleCopy> = {
  admin: {
    label: 'Administrator',
    who:
      'The register’s owner in the department. Grants roles and suspends accounts, and keeps ' +
      'the department’s working calendars and settings.',
    not: 'Does no engineering work unless also granted the role for it.',
  },
  planner: {
    label: 'Planner',
    who:
      'The contractor’s or consultant’s planning engineer. Creates a planned structure, takes and ' +
      'revises the baseline programme, and matches it to the model.',
    not: 'Cannot approve their own baseline, or record or review works.',
  },
  site: {
    label: 'Site agent',
    who:
      'The contractor’s foreman or works inspector. Records what was built — works, daily ' +
      'returns, incidents, documents and test results — from the field app or the web app.',
    not: 'Cannot review their own entries, log an inspection or an issue, or change the register.',
  },
  resident: {
    label: 'Resident engineer',
    who:
      'The supervision consultant on site. Reviews site records, releases hold points, raises ' +
      'and closes non-conformance reports, closes incidents, and prepares the handover.',
    not: 'Cannot accept the handover, or accept a record they made themselves.',
  },
  inspector: {
    label: 'Inspector',
    who:
      'The bridge inspector. Logs inspections and issues — measurements and photographs, from ' +
      'the field app or the web app — and verifies a repair on a later visit.',
    not: 'Cannot accept an inspection, move an issue through repair, or correct the inventory.',
  },
  engineer: {
    label: 'Bridge engineer',
    who:
      'The divisional or maintenance engineer. Accepts inspections, moves issues through ' +
      'repair, and keeps the inventory, the models and the approved rates.',
    not: 'Cannot compute or accept a rating, or grant roles.',
  },
  rater: {
    label: 'Rating engineer',
    who: 'The engineer who runs load ratings. Computes a rating and sends it for review.',
    not: 'Cannot accept a rating.',
  },
  approver: {
    label: 'Approver',
    who:
      'The department’s representative who decides. Accepts baselines, revisions, extensions of ' +
      'time, handovers and ratings, and restricts, lifts, closes and reopens a bridge.',
    not: 'Cannot accept anything they made themselves.',
  },
};

/**
 * A set of roles in the words a person reads: "Inspector · Rating engineer".
 *
 * The two states that are not roles are said as what they mean rather than as a
 * blank: no roles reads the register, and a suspended account may do nothing.
 */
export function rolesLabel(roles: readonly Role[], suspended = false): string {
  if (suspended) return 'Suspended';
  if (roles.length === 0) return 'No role · reads only';
  return ROLES.filter((role) => roles.includes(role))
    .map((role) => ROLE_COPY[role].label)
    .join(' · ');
}

/* --------------------------- a person's row --------------------------- */

/**
 * An account and the roles granted to it.
 *
 * `email` is the username, lowercased, or `service-token:<client id>` for a
 * machine — the same string `identityOf` calls the author, so an account and the
 * rows it authored are keyed alike and one query joins them.
 */
export interface Person {
  email: string;
  /** Every role held, in ROLES order. Empty is an account that reads only. */
  roles: Role[];
  /** Suspended: may do nothing, including read. The roles are kept for when it is lifted. */
  suspended: boolean;
  /** As they would like to be named on screen, or null where nobody typed it. */
  fullName: string | null;
  /**
   * The division or district they work in.
   *
   * A NOTE ABOUT THEM, NOT A LIMIT ON THEM. Nothing anywhere reads this to
   * decide a permission — see the header of this file for why not.
   */
  division: string | null;
  /** Why these roles were granted, for whoever inherits the register. */
  note: string | null;
  /** The administrator who last changed this account, and when. Null is "not attributable". */
  grantedBy: string | null;
  grantedAt: string;
  /**
   * True where the account is an administrator through the deployment's own
   * ADMIN_EMAILS rather than a grant. Its `admin` role cannot be withdrawn or
   * suspended from inside the application — the bootstrap, and the way out of a
   * locked-out register — but it may be granted other roles like anybody else.
   */
  fromDeployment?: boolean;
}

/**
 * A username this register can grant a role to.
 *
 * AN EMAIL ADDRESS OR A PLAIN NAME. This required an `@` while Cloudflare Access
 * was the authenticator, and it had to: the identity was an address Access had
 * verified, so anything else could never match. The register keeps its own
 * usernames now, `scripts/make-admin.ts` has always accepted a plain one, and
 * this function refusing them made the two disagree — a bootstrap administrator
 * called `j.manuel` could sign in and could not be granted a role by the screen
 * that grants roles.
 *
 * The character set is the one the bootstrap script allows, for the same reason
 * it is narrow there: this string is the key of every record its holder authors,
 * it appears in a URL query on the People screen, and it is interpolated into SQL
 * by that script. Three characters long at least, because a one-letter username
 * is a typo more often than a person.
 */
export function isGrantableIdentity(value: string): boolean {
  const text = value.trim().toLowerCase();
  if (text.startsWith('service-token:')) return text.length > 'service-token:'.length;
  return /^[a-z0-9._%+@-]{3,200}$/.test(text) && !text.includes('@@');
}

/** Lowercased and trimmed, the one form an identity is stored and compared in. */
export function normaliseIdentity(value: string): string {
  return value.trim().toLowerCase();
}
